import * as React from "react";
import clsx from "clsx";
import { BarChart3, Calendar, FilterX, MessageSquare, Trash2 } from "lucide-react";
import type { Ordering } from "@/types/base";
import { Button } from "@/components/ui/button";
import { SortDropdown, type SortOption } from "@/components/sortDropdown";
import * as analytics from "@/services/analytics";
import { SnapshotGridViewSearch } from "@/modules/snapshots/grid/search";
import { ColumnVisibilityDropdown } from "@/modules/snapshots/grid/columnVisibilityDropdown";
import type { DateFilter, SnapshotGridSortField } from "@/modules/snapshots/grid/sortAndFilter";
import type { SnapshotGridCapabilities } from "@/modules/snapshots/grid/dataSource";
import { SnapshotCollectionsOutcomesToolbar } from "@/modules/snapshots/snapshotCollections/components/outcomes/list/toolbar";
import type { Subcollection } from "@/modules/snapshots/snapshotCollection.types";

interface Props {
  hidden: boolean;
  gridCapabilities: SnapshotGridCapabilities;
  isGridResultStale: boolean;
  onToggleQaPanel: () => void;
  isPivotDataComplete: boolean;
  pivotUnavailableMessage: string;
  onOpenPivotBuilder: () => void;
  selectedSnapshots: string[];
  snapshotCollectionId: string;
  subcollectionId: string | undefined;
  onSubcollectionCreated: (subcollection: Subcollection) => void;
  isContractFamilyProjection: boolean;
  /** The Supplier Summaries Collection: its columns come from the supplier template, not from here. */
  isFamilyCollection?: boolean;
  onDeleteSelected: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  isFetching: boolean;
  activeColumnFilterCount: number;
  onClearAllColumnFilters: () => void;
  hasDatePrompts: boolean;
  dateFilter: DateFilter;
  onOpenDateFilterModal: () => void;
  sortFields: SnapshotGridSortField[];
  currentOrdering: Ordering;
  onSortChange: (sort: SortOption) => void;
  allColumnHeaders: string[];
  hiddenColumns: string[];
  onHiddenColumnsChange: (columns: string[]) => void;
  isOwner: boolean;
  onAddColumnClick: () => void;
}

/** Table tab actions: analyse, pivot, sub-collection and bulk delete, search, filters, sort, columns. */
export const SnapshotGridToolbar: React.FC<Props> = ({
  hidden,
  gridCapabilities,
  isGridResultStale,
  onToggleQaPanel,
  isPivotDataComplete,
  pivotUnavailableMessage,
  onOpenPivotBuilder,
  selectedSnapshots,
  snapshotCollectionId,
  subcollectionId,
  onSubcollectionCreated,
  isContractFamilyProjection,
  isFamilyCollection = false,
  onDeleteSelected,
  query,
  onQueryChange,
  isFetching,
  activeColumnFilterCount,
  onClearAllColumnFilters,
  hasDatePrompts,
  dateFilter,
  onOpenDateFilterModal,
  sortFields,
  currentOrdering,
  onSortChange,
  allColumnHeaders,
  hiddenColumns,
  onHiddenColumnsChange,
  isOwner,
  onAddColumnClick,
}) => {
  const hasActiveDateFilter = Boolean(dateFilter.selectedField && (dateFilter.startDate || dateFilter.endDate));

  return (
    <div className={clsx("mb-4 flex items-center justify-between", hidden && "hidden")}>
      <div className="flex items-center gap-2">
        {gridCapabilities.analyse && (
          <Button
            variant="coral"
            size="sm"
            disabled={isGridResultStale}
            onClick={() => {
              analytics.snapshotAnalysis();
              onToggleQaPanel();
            }}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            Analyse
          </Button>
        )}
        {gridCapabilities.pivot && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenPivotBuilder}
            disabled={isGridResultStale || !isPivotDataComplete}
            title={!isPivotDataComplete ? pivotUnavailableMessage : undefined}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Pivot Table
          </Button>
        )}
        {gridCapabilities.subcollections && (
          <SnapshotCollectionsOutcomesToolbar
            selectedIds={new Set(selectedSnapshots)}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            buttonSize="sm"
            onSubcollectionCreated={onSubcollectionCreated}
            disabled={isGridResultStale}
            contractFamilyProjection={isContractFamilyProjection}
          />
        )}
        {selectedSnapshots.length > 0 && (gridCapabilities.deleteRows || !!subcollectionId) && (
          <Button variant="destructive" onClick={onDeleteSelected} size="sm" disabled={isGridResultStale}>
            <Trash2 className="mr-2 h-4 w-4" />
            {subcollectionId ? "Remove" : "Delete"} {selectedSnapshots.length} Snapshot
            {selectedSnapshots.length !== 1 ? "s" : ""}
          </Button>
        )}
        <SnapshotGridViewSearch isFetching={isFetching} setQuery={onQueryChange} query={query} />
      </div>

      <div className="justify-content-between flex items-center gap-2">
        {activeColumnFilterCount > 0 && (
          <Button variant="outline" size="sm" onClick={onClearAllColumnFilters} className="gap-2">
            <FilterX className="h-4 w-4" />
            Clear filters
            <span className="ml-1 rounded-full bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
              {activeColumnFilterCount}
            </span>
          </Button>
        )}
        {hasDatePrompts && (
          <Button
            variant={hasActiveDateFilter ? "default" : "outline"}
            size="sm"
            onClick={onOpenDateFilterModal}
            className="gap-2"
          >
            <Calendar className="h-4 w-4" />
            Filter by Date
            {hasActiveDateFilter && (
              <span className="ml-1 rounded-full bg-primary-foreground px-1.5 py-0.5 text-xs text-primary">
                {dateFilter.selectedField}
              </span>
            )}
          </Button>
        )}
        <SortDropdown
          className="mb-0"
          fields={sortFields}
          currentSort={{
            field: currentOrdering.order_by,
            direction: currentOrdering.descending ? "desc" : "asc",
          }}
          onSortChange={onSortChange}
        />
        {allColumnHeaders.length > 0 && (
          <ColumnVisibilityDropdown
            columns={allColumnHeaders}
            hiddenColumns={hiddenColumns}
            onHiddenColumnsChange={onHiddenColumnsChange}
          />
        )}
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAddColumnClick}
            disabled={!gridCapabilities.aiColumns || isFamilyCollection}
            title={
              isFamilyCollection
                ? "Add columns to the Supplier Standard Template; they reach the summaries on the next run"
                : !gridCapabilities.aiColumns
                  ? "AI columns are disabled for synthetic family summary rows."
                  : undefined
            }
          >
            Add column
          </Button>
        )}
      </div>
    </div>
  );
};
